import configureStore from "./store/configureStore";
import { PubSubContext } from "./App";
import { Provider } from "react-redux";
import type React from "react";
import { useState, createContext } from "react";
import { THEME_LIGHT, THEME_DARK } from "./App.constant";
import { createTheme, ThemeProvider, type ThemeOptions } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import ProviderLanguage from "./components/ProviderLanguage";

export const store = configureStore(undefined);

/** The CSS custom-property names one theme maps to. */
export type ThemeVars = {
  rootBackground: string;
  rootTextColor: string;
  /** Only the light theme defines this one. */
  userNameTagComponentBackground?: string;
};

export const themes: Record<string, ThemeVars> = {
  [THEME_LIGHT]: {
    rootBackground: "--surface-neutral",
    rootTextColor: "--primary-text",
    userNameTagComponentBackground: "--light-blue-tag",
  },
  [THEME_DARK]: {
    rootBackground: "--surface-black",
    rootTextColor: "--surface-neutral",
  },
};

/**
 * Everything the Provider below actually supplies. The default value carries only `theme`, so the
 * other three are optional — declaring them here is what lets Header / profileSideBar / PortalDashboard
 * read `themeType` and `setTheme` without asserting an intersection at every use.
 */
export type ThemeContextValue = {
  theme: ThemeVars;
  themes?: Record<string, ThemeVars>;
  themeType?: string;
  setTheme?: React.Dispatch<React.SetStateAction<string>>;
};

export const ThemeContext = createContext<ThemeContextValue>({
  // Erased `!`: `themes` is indexed by theme name, and the light theme is always present.
  theme: themes[THEME_LIGHT]!,
});

/** Tailwind's `max-md`: every phone width, the 750px landscape one included. */
const PHONE_AND_LANDSCAPE_MEDIA = "@media (max-width:47.99875em)";

const muiThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: "inherit",
  },
  zIndex: {
    modal: 1200,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiPopper: {
      defaultProps: {
        style: { zIndex: 1400 },
      },
    },
    MuiPopover: {
      defaultProps: {
        style: { zIndex: 1400 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          zIndex: "1501 !important",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        // Phones only (below Tailwind's md, 768px, so the 750px landscape run too; media queries take em at the
        // 16px initial font). The content box is a flex item beside the expand icon, so its default
        // `min-width: auto` let a long heading (or one unbroken token) push the content and the icon past the
        // summary's edge. `overflow-wrap: anywhere` is inherited by everything in the summary, so from md up,
        // where the headings have room, a crowded row keeps breaking only between words.
        content: {
          [PHONE_AND_LANDSCAPE_MEDIA]: {
            minWidth: 0,
            overflowWrap: "anywhere",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          "& .MuiTabs-indicator": {
            backgroundColor: "#6BBBE3",
            height: "0.25rem",
            borderRadius: "0.125rem",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          padding: "0",
          minWidth: "auto",
          marginRight: "1.5rem", // 6 * 4px (default spacing unit)
          textTransform: "none",
          color: "var(--primary-text)",
          fontWeight: 400,

          // Selected state styling
          "&.Mui-selected": {
            fontWeight: 600,
            color: "var(--primary-text)",
          },
        },
      },
    },
  },
};

export const muiTheme = createTheme(muiThemeOptions);
// `direction` is what turns on MUI's RTL context (ThemeProvider → RtlProvider): it flips Drawer
// anchors (the app sidebar), Popper/Menu placements and Pagination arrows.
const muiRtlTheme = createTheme({ ...muiThemeOptions, direction: "rtl" });
// MUI writes physical left/right styles (the sidebar Drawer's `left: 0`, input adornments, …) and
// relies on this stylis plugin to mirror them in RTL. One provider switching caches keeps the app
// tree mounted when the language lands at boot.
const ltrEmotionCache = createCache({ key: "mui" });
const rtlEmotionCache = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin] });

function ChildProviderApp({ children }: Readonly<{ children?: React.ReactNode }>) {
  const [theme, setTheme] = useState(THEME_LIGHT);
  const [isRtl, setIsRtl] = useState(false);

  return (
    <ProviderLanguage
      isRTLCallback={(isRTL: boolean) => {
        document.documentElement.dir = isRTL ? "rtl" : "ltr";
        setIsRtl(isRTL);
      }}
    >
      <CacheProvider value={isRtl ? rtlEmotionCache : ltrEmotionCache}>
        <ThemeProvider theme={isRtl ? muiRtlTheme : muiTheme}>
          {/* KNOWN BUG, preserved: this Provider shadows the context default (`{ PubSubHelper }`), so
            any consumer reading `PubSubHelper` under it gets `undefined` rather than the ambient
            helper. Supplied as a conforming object — `PubSubHelper` is declared `unknown`, so no cast
            is needed and destructuring the context value no longer throws. */}
          <PubSubContext.Provider value={{ PubSubHelper: undefined }}>
            <ThemeContext.Provider
              value={{
                // Erased `!`: `theme` is always one of the two keys declared above.
                theme: themes[theme]!,
                setTheme,
                themes,
                themeType: theme,
              }}
            >
              {children}
            </ThemeContext.Provider>
          </PubSubContext.Provider>
        </ThemeProvider>
      </CacheProvider>
    </ProviderLanguage>
  );
}

function ProviderApp({ children }: Readonly<{ children?: React.ReactNode }>) {
  return (
    <Provider store={store}>
      <ChildProviderApp>{children}</ChildProviderApp>
    </Provider>
  );
}

export default ProviderApp;
